import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import LogicBranch from 'common/icons/LogicBranch';
import Trash from 'common/icons/Trash';

import {
    STATUS_LOADING,
    STATUS_READY,
    SuiButton,
    SuiConfirmDialogWrap,
    SuiContainer,
    SuiTooltipPopover,
} from '@splashthat/sui';

import { getCanUserDeleteRegistrationForm } from 'common/store/permissions';
import { getCanUserUpdateLibraryFormsUserPermission } from 'common/store/userPermissions/selectors';
import PermissionCheckWrapper from 'utils/PermissionCheckWrapper';
import { getFormNodeEditingLocked } from 'common/entities/formNode/selectors';
import { setFreemiumSelectedModal } from '../../actions';
import { getEditFormNodeId, getEditFormNodeLabel, getLibraryFlag, getSavingQuestionToBank } from '../../selectors';
import { MODAL_LIBRARY } from '../../constants/modals';

import EditViewSaveQuestion from './EditViewSaveQuestion';
import './EditViewFooter.styl';
import { getRemovalConfirmation } from '../LayoutView/FormEditorNodeControls';

const EDIT_LOGIC_LABEL = 'Edit Logic';
const DELETE_TITLE = 'Delete';
const BASE_COMPONENT_NAME = 'EditViewFooter';

const EditViewFooter = (props) => (
    <SuiContainer className={BASE_COMPONENT_NAME} variation="flex-no-shrink flex-horizontal-centered standard">
        {props.onClickEditLogic && !props.editingLocked && (
            <SuiContainer variation="flex-group">
                <SuiTooltipPopover content={EDIT_LOGIC_LABEL} openDelayed>
                    <SuiButton
                        data-qa="FORM_QUESTION_CONDITIONAL_LOGIC"
                        variation="icon secondary"
                        size="small"
                        icon={<LogicBranch />}
                        onClick={props.onClickEditLogic}
                    />
                </SuiTooltipPopover>
            </SuiContainer>
        )}

        {props.onClickSaveNode && !props.editingLocked && (
            <SuiContainer variation="flex-group">
                <EditViewSaveQuestion
                    onClickSaveNode={props.onClickSaveNode}
                    bookmarkButtonStatus={props.bookmarkButtonStatus}
                    initialValue={props.nodeLabel}
                    blocked={!props.libraryFlag}
                    onBlocked={() => props.setFreemiumSelectedModal(MODAL_LIBRARY)}
                />
            </SuiContainer>
        )}

        {props.onClickDelete && !props.editingLocked && (
            <SuiContainer variation="flex-group">
                <SuiConfirmDialogWrap
                    dialogBody={getRemovalConfirmation(props.nodeType)}
                    onConfirm={props.onClickDelete}
                >
                    {(onClick) => (
                        <PermissionCheckWrapper
                            permission={props.canUserDeleteRegistrationForm || props.canUserEditLibraryForms}
                        >
                            {(restProps) => (
                                <SuiTooltipPopover content={DELETE_TITLE} openDelayed>
                                    <SuiButton
                                        variation="icon secondary"
                                        size="small"
                                        icon={<Trash />}
                                        title={DELETE_TITLE}
                                        aria-label={DELETE_TITLE}
                                        data-qa={`${BASE_COMPONENT_NAME}__delete-button`}
                                        onClick={onClick}
                                        disabled={
                                            !props.canUserDeleteRegistrationForm && !props.canUserEditLibraryForms
                                        }
                                        {...restProps}
                                    />
                                </SuiTooltipPopover>
                            )}
                        </PermissionCheckWrapper>
                    )}
                </SuiConfirmDialogWrap>
            </SuiContainer>
        )}
    </SuiContainer>
);

EditViewFooter.propTypes = {
    bookmarkButtonStatus: PropTypes.number.isRequired,
    libraryFlag: PropTypes.bool.isRequired,
    setFreemiumSelectedModal: PropTypes.func.isRequired,
    nodeLabel: PropTypes.string.isRequired,
    onClickEditLogic: PropTypes.func,
    onClickSaveNode: PropTypes.func,
    onClickDelete: PropTypes.func,
    editingLocked: PropTypes.bool.isRequired,
    nodeType: PropTypes.string,
    canUserDeleteRegistrationForm: PropTypes.bool.isRequired,
    canUserEditLibraryForms: PropTypes.bool.isRequired,
};

EditViewFooter.defaultProps = {
    onClickEditLogic: null,
    onClickSaveNode: null,
    onClickDelete: null,
    nodeType: null,
};

const mapStateToProps = (state) => {
    const editFormNodeId = getEditFormNodeId(state);

    return {
        libraryFlag: getLibraryFlag(state),
        nodeLabel: getEditFormNodeLabel(state) || '',
        bookmarkButtonStatus: getSavingQuestionToBank(state) ? STATUS_LOADING : STATUS_READY,
        editingLocked: editFormNodeId ? getFormNodeEditingLocked(state, editFormNodeId) : false,
        canUserDeleteRegistrationForm: getCanUserDeleteRegistrationForm(state),
        canUserEditLibraryForms: getCanUserUpdateLibraryFormsUserPermission(state),
    };
};

const mapDispatchToProps = {
    setFreemiumSelectedModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(EditViewFooter);
